import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./CoursesPage.css";
import axios from "axios";

const CourseRegistrationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const course = location.state || {};

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    nationality: "",
    country: "",
    modeOfLearning: "",
    preferredSchedule: [],
    hasExperience: "",
    experienceDetails: "",
    referralSource: "",
    otherReferral: "",
    questions: "",
    agreeToUpdates: false,
    courseName: course.courseTitle || "",
    coursePrice: "",  // initially set to empty
  });

  const [loading, setLoading] = useState(false)
  const [orderReference, setOrderReference] = useState(null);
  const [paymentInitialized, setPaymentInitialized] = useState(false);

  // Generate unique order reference
  const generateOrderReference = () => {
    return 'ORD' + Date.now();
  };

  // Setup OnePay configuration
  const setupOnePay = (reference) => {
    // Parse price - handle various formats
    const priceString = formData.coursePrice?.toString() || '0';
    const parsedPrice = parseFloat(priceString.replace(/[^0-9.]/g, ""));

    if (isNaN(parsedPrice) || parsedPrice <= 0) {
      throw new Error('Invalid price amount');
    }

    // Split full name safely
    const nameParts = (formData.fullName || '').trim().split(/\s+/);
    const firstName = nameParts[0] || 'Customer';
    const lastName = nameParts.slice(1).join(' ') || '';

    // OnePay Data configuration
    const onePayData = {
      appid: "80NR1189D04CD635D8ACD",
      hashToken: "GR2P1189D04CD635D8AFD",
      amount: parsedPrice,
      orderReference: reference,
      customerFirstName: firstName,
      customerLastName: lastName,
      customerPhoneNumber: formData.phone,
      customerEmail: formData.email,
      transactionRedirectUrl: `${window.location.origin}/payment/success`,
      additionalData: JSON.stringify(formData),
      currency: 'LKR',
      apptoken: "ca00d67bf74d77b01fa26dc6780d7ff9522d8f82d30ff813d4c605f2662cea9ad332054cc66aff68.EYAW1189D04CD635D8B20"
    };

    console.log('OnePay Configuration:', onePayData);
    window.onePayData = onePayData;

    return onePayData;
  };

  // Setup payment event listeners
  useEffect(() => {
    const handlePaymentSuccess = async (e) => {
      console.log("Payment SUCCESS:", e.detail);

      try {
        // Save the order data with payment success
        const orderData = {
          ...formData,
          orderReference: orderReference,
          paymentStatus: 'SUCCESS',
          transactionId: e.detail?.transaction_id,
          paymentCode: e.detail?.code,
          paymentDate: new Date().toISOString()
        };
        console.log('Order completed:', orderData);

        // Redirect to success page
        navigate(`/payment/success?ref=${orderReference}&txn=${e.detail?.transaction_id}`);

      } catch (err) {
        console.error('Error processing payment success:', err);
        alert('Payment successful but failed to save order. Please contact support with reference: ' + orderReference);
      }
    };

    const handlePaymentFail = async (e) => {
      console.log("Payment FAIL:", e.detail);

      try {
        // Log the failed payment
        const failedOrderData = {
          ...formData,
          orderReference: orderReference,
          paymentStatus: 'FAILED',
          transactionId: e.detail?.transaction_id || null,
          paymentCode: e.detail?.code,
          paymentDate: new Date().toISOString()
        };

        console.log('Payment failed:', failedOrderData);


        // Redirect to failure page
        navigate(`/payment/failed?ref=${orderReference}`);

      } catch (err) {
        console.error('Error processing payment failure:', err);
      }
    };

    window.addEventListener("onePaySuccess", handlePaymentSuccess);
    window.addEventListener("onePayFail", handlePaymentFail);

    return () => {
      window.removeEventListener("onePaySuccess", handlePaymentSuccess);
      window.removeEventListener("onePayFail", handlePaymentFail);
    };
  }, [orderReference, formData, navigate]);

  // Auto-trigger payment when initialized
  useEffect(() => {
    if (paymentInitialized && !loading) {
      const timer = setTimeout(() => {
        if (window.onPayButtonClicked) {
          console.log('Auto-triggering OnePay payment...');
          window.onPayButtonClicked();
        } else {
          console.error('OnePay button not found');
          setLoading(false);
          setPaymentInitialized(false);
        }
      }, 1000); // Wait 1 second for OnePay to fully initialize

      return () => clearTimeout(timer);
    }
  }, [paymentInitialized, loading]);

  useEffect(() => {
    // Update price when nationality changes
    const updatedPrice =
      formData.nationality === "International"
        ? course.courseFeesIS
        : course.courseFeesLS;
    setFormData((prevData) => ({ ...prevData, coursePrice: updatedPrice || "" }));
  }, [formData.nationality, course.courseFeesIS, course.courseFeesLS]);

  const handleNationalityChange = (e) => {
    setFormData({ ...formData, nationality: e.target.value });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleMultiSelect = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked
        ? [...prevData[name], value]
        : prevData[name].filter((option) => option !== value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form Submitted:", formData);
    try {
      const reference = generateOrderReference();
      setOrderReference(reference)
      console.log("Processing order with reference:", reference);
      // Setup OnePay payment
      setupOnePay(reference);

      // Initialize payment
      setPaymentInitialized(true);

    } catch (error) {
      console.error('Payment initialization error:', error);
      setLoading(false);
      setPaymentInitialized(false);
    } finally {

    }

  };



  return (
    <div className="container registration-container">
      <h1>Enrollment Form for {formData.courseName || "Psychology Course"}</h1>
      <p>Welcome to Helping Hand Psychological Hospital! Please fill out this form, and we’ll get back to you with more details.</p>

      <form onSubmit={handleSubmit}>
        <h2>Personal Information</h2>
        <label>
          Full Name:
          <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required />
        </label>
        <label>
          Email Address:
          <input type="email" name="email" value={formData.email} onChange={handleChange} required />
        </label>
        <label>
          Phone Number:
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
        </label>

        <label>
          Nationality:
          <select name="nationality" value={formData.nationality} onChange={handleNationalityChange} required>
            <option value="" disabled>Select Nationality</option>
            <option value="National">Sri Lankan</option>
            <option value="International">International</option>
          </select>
        </label>

        <label>
          Country of Residence:
          <input type="text" name="country" value={formData.country} onChange={handleChange} required />
        </label>

        <h2>Course Details</h2>
        <div>Course Name: <strong>{formData.courseName}</strong></div>

        {/* Only show course fee once a nationality has been selected */}
        {formData.nationality && formData.coursePrice && (
          <div>Course Fee: <strong>{formData.coursePrice}</strong></div>
        )}

        <label>Preferred Mode of Learning:</label>
        <div className="checkbox-group">
          <label>
            <input type="radio" name="modeOfLearning" value="Online" onChange={handleChange} />
            <strong> Online</strong>
          </label>
          <label>
            <input type="radio" name="modeOfLearning" value="In-Person" onChange={handleChange} />
            <strong> In-Person</strong>
          </label>
          <label>
            <input type="radio" name="modeOfLearning" value="Hybrid" onChange={handleChange} />
            <strong> Hybrid</strong>
          </label>
        </div>

        <label>Preferred Schedule:</label>
        <div className="checkbox-group">
          <label>
            <input type="checkbox" name="preferredSchedule" value="Weekdays Morning" onChange={handleMultiSelect} />
            <strong> Weekdays (Morning)</strong>
          </label>
          <label>
            <input type="checkbox" name="preferredSchedule" value="Weekdays Evening" onChange={handleMultiSelect} />
            <strong> Weekdays (Evening)</strong>
          </label>
          <label>
            <input type="checkbox" name="preferredSchedule" value="Flexible" onChange={handleMultiSelect} />
            <strong> Flexible</strong>
          </label>
        </div>

        <label>Do you have prior experience in psychology?</label>
        <div className="checkbox-group">
          <label>
            <input type="radio" name="hasExperience" value="Yes" onChange={handleChange} />
            <div><strong className="experience-res">Yes</strong> </div>
          </label>
          <label>
            <input type="radio" name="hasExperience" value="No" onChange={handleChange} />
            <div><strong className="experience-res">No</strong> </div>
          </label>
        </div>

        {formData.hasExperience === "Yes" && (
          <label>Describe your experience:
            <textarea name="experienceDetails" value={formData.experienceDetails} onChange={handleChange}></textarea>
          </label>
        )}

        <h2>Additional Information</h2>
        <label>
          How did you hear about us?
          <select name="referralSource" value={formData.referralSource} onChange={handleChange}>
            <option value="">Select an option</option>
            <option value="Social Media">Social Media</option>
            <option value="Website">Website</option>
            <option value="Referral">Referral</option>
            <option value="Advertisement">Advertisement</option>
            <option value="Other">Other</option>
          </select>
        </label>

        {formData.referralSource === "Other" && (
          <label>Please specify:
            <input type="text" name="otherReferral" value={formData.otherReferral} onChange={handleChange} />
          </label>
        )}

        <label>
          Do you have any questions or special requests?
          <textarea name="questions" value={formData.questions} onChange={handleChange}></textarea>
        </label>

        <label className="agree-checkbox">
          <input
            type="checkbox"
            name="agreeToUpdates"
            checked={formData.agreeToUpdates}
            onChange={handleChange}
          />
          I agree to receive updates and promotions.
        </label>
        <button disabled={loading} type="submit">{loading || paymentInitialized ? 'Processing Payment...' : 'Proceed to Payment'}</button>

      </form>
      <div style={{ margin: "20px" }}>
        <div id="onepay-btn" style={{ display: 'none' }} />
        <div id="iframe-container" />
      </div>
    </div>
  );
};

export default CourseRegistrationForm;
